import { Button } from '@headlessui/react';
import { EnvelopeIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { sendEmailVerification, signOut } from "firebase/auth";
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { auth } from "../firebase";

export const EmailVerificationOverlay = ({ user }) => {
  const [isResending, setIsResending] = useState(false);

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      await sendEmailVerification(user, {
        url: `${window.location.origin}/email-verified`,
        handleCodeInApp: false
      });
      toast.success('Verification email sent! Please check your inbox.', {
        duration: 6000,
      });
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast.error('Failed to resend email. Please try again later.');
    } finally {
      setIsResending(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await user.reload();
      if (user.emailVerified) {
        toast.success('Email verified! Refreshing...');
        window.location.reload();
      } else {
        toast.error('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      toast.error('Failed to check verification status. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#4A4A4A]/20 backdrop-blur-md border border-white/20 rounded-[50px] p-8 max-w-md w-full text-center text-white">
        <div className="flex justify-center mb-6">
          <div className="bg-[#7BCDBA]/20 rounded-full p-4">
            <EnvelopeIcon className="w-8 h-8 text-[#7BCDBA]" />
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-3">Verify Your Email</h2>

        <p className="text-white/80 mb-2">
          We've sent a verification email to:
        </p>
        <p className="text-[#7BCDBA] font-semibold mb-6">
          {user.email}
        </p>

        <p className="text-white/70 text-sm mb-2">
          Please check your inbox and click the verification link. After verifying, click the refresh button below to continue.
        </p>

        <div className="bg-white/5 rounded-lg px-3 py-2 mb-6 flex items-center gap-2 text-center justify-center">
          <ExclamationTriangleIcon className="w-4 h-4 text-white/40 flex-shrink-0 text-orange-400" />
          <p className="text-white/50 text-xs">
            Please check your <strong>spam folder</strong>
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleRefresh}
            className="w-full py-3 bg-[#7BCDBA] text-black rounded-full hover:bg-[#6bbdaa] transition-colors font-medium flex items-center justify-center gap-2">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh to Check Verification
          </Button>

          <Button
            onClick={handleResendVerification}
            disabled={isResending}
            className="w-full py-3 bg-transparent border border-white/30 text-white rounded-full hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium">
            {isResending ? 'Sending...' : 'Resend Verification Email'}
          </Button>

          <Button
            onClick={handleSignOut}
            className="w-full py-2 text-sm text-white/60 hover:text-white transition-colors">
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};
